import React, { useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../../shadcn/components/ui/Dialog';
import { Badge } from '../../../shadcn/components/ui/Badge';
import { Alert, AlertDescription } from '../../../shadcn/components/ui/Alert';
import { Card, CardContent, CardHeader, CardTitle } from '../../../shadcn/components/ui/Card';
import { Button } from '../../../shadcn/components/ui/Button';
import { AlertCircle, Package, Calendar, Clock, Download, Snowflake } from 'lucide-react';
import { downloadCSV } from '../../../shared/components/csvDownload';
import { opsVisibilityApi as api } from '../../../api/opsVisibility';

const FrozenManifestModal = ({ open, onOpenChange, manifestData, setNotification, facilityID }) => {
    const { manifest } = manifestData || {};
    const hasShownErrorRef = useRef(false);
    
    // Use useEffect to handle error notification to prevent infinite re-renders
    // Only show error when modal is actually open and we have invalid data
    useEffect(() => {
        const hasError = !manifestData || !manifest || !manifest.line_haul_id;
        
        // Only show error if modal is open (not during initial loading)
        if (open && hasError && !hasShownErrorRef.current && setNotification) {
            const errorMessage = 'Unable to display Frozen Manifest: Missing required manifest data or line haul ID';
            console.error('FrozenManifestModal Error:', {
                manifestData: !!manifestData,
                manifest: !!manifest,
                lineHaulId: manifest?.line_haul_id || 'missing',
                error: errorMessage
            });
            
            setNotification({ 
                text: errorMessage, 
                level: 'error' 
            });
            
            hasShownErrorRef.current = true;
        } else if (!hasError || !open) {
            // Reset the error flag when we have valid data or modal is closed
            hasShownErrorRef.current = false;
        }
    }, [open, manifestData, manifest, setNotification]);
    
    if (!manifestData || !manifest || !manifest.line_haul_id) {
        return null;
    }

    const hasRoutes = manifest.routes && manifest.routes.length > 0;

    const handleDownloadCSV = () => {
        // Double check for routes and provide feedback if none exist
        if (!manifest.routes || manifest.routes.length === 0) {
            if (setNotification) {
                setNotification({ text: 'No routes available to download. The manifest is empty.', level: 'warning' });
            } else {
                alert('No routes available to download. The manifest is empty.');
            }
            return;
        }

        // Create CSV headers for route summary data
        const headers = ['Route', 'Shipment Count'];
        let csvContent = headers.join(',') + '\r\n';

        // Add data rows
        manifest.routes.forEach(route => {
            const row = [
                `"${(route.route || '').replace(/"/g, '""')}"`, // Escape quotes in route names
                route.shipmentCount || 0
            ];
            csvContent += row.join(',') + '\r\n';
        });

        // Generate filename with line haul and pack date
        const filename = `Frozen_Manifest_LineHaul_${manifest.line_haul_id}_${manifest.expected_pack_date}.csv`;
        
        downloadCSV(csvContent, filename);
    };

    const handleDownloadDetailedCsvForRoute = async (route) => {
        try {
            const expectedPackDate = manifest.expected_pack_date;
            const lineHaulId = manifest.line_haul_id;
            if (!route || !expectedPackDate || !lineHaulId) {
                setNotification?.({ text: 'Missing required parameters to download CSV', level: 'error' });
                return;
            }
            const resp = await api.downloadFrozenManifestByRouteCsv({
                lineHaulId,
                expectedPackDate,
                route,
            });
            if (!resp?.status) {
                setNotification?.({ text: resp?.msg || 'Failed to download CSV', level: 'error' });
            }
        } catch (e) {
            setNotification?.({ text: e.message || 'Failed to download CSV', level: 'error' });
        }
    };

    return (
        <Dialog 
            open={open} 
            onOpenChange={onOpenChange}
            modal={true}
        >
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Snowflake className="h-5 w-5 text-blue-400" />
                        Frozen Manifest - Line Haul {manifest.line_haul_id}
                    </DialogTitle>
                    <DialogDescription>
                        Pack Group {manifest.pack_group_id} • Week {manifest.week}, {manifest.year}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Important Notice */}
                    <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                            <strong>Important:</strong> This manifest shows a summary of frozen shipments grouped by route. 
                            Use the "Detailed CSV" button for each route to download individual shipment details (order, shipment, stop number).
                        </AlertDescription>
                    </Alert>

                    {/* Summary Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-blue-500" />
                                    Expected Pack Date
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold">{manifest.expected_pack_date}</p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                    <Package className="h-4 w-4 text-green-500" />
                                    Total Shipments
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-2xl font-bold">{manifest.total_shipments}</p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-purple-500" />
                                    Generated At
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm font-medium">{manifest.generated_at}</p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Routes or No Routes Message */}
                    {!hasRoutes ? (
                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                No routes found for this line haul and pack date combination. 
                                The manifest is empty for Line Haul {manifest.line_haul_id} on {manifest.expected_pack_date}.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold">Routes ({manifest.total_routes}) - {manifest.total_shipments} Total Shipments</h3>
                                <div className="flex items-center gap-2">
                                    <Button 
                                        onClick={handleDownloadCSV}
                                        variant="outline" 
                                        size="sm"
                                        className="flex items-center gap-2"
                                    >
                                        <Download className="h-4 w-4" />
                                        Download Summary CSV
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="overflow-x-auto">
                                <table className="w-full border border-gray-200 rounded-lg">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border-b">
                                                Route
                                            </th>
                                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-900 border-b">
                                                Shipment Count
                                            </th>
                                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-900 border-b">
                                                Export Details
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {manifest.routes.map((routeItem, index) => (
                                            <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    <Badge variant="outline">{routeItem.route}</Badge>
                                                </td>
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900 text-center">
                                                    {routeItem.shipmentCount}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right">
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        onClick={() => handleDownloadDetailedCsvForRoute(routeItem.route)}
                                                        className="flex items-center gap-2 ml-auto"
                                                    >
                                                        <Download className="h-4 w-4" /> Detailed CSV
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export { FrozenManifestModal };

